// Arcade state for the current person: progress, today's pool of plays, and the active
// capsule (kept in sessionStorage so a reload resumes it instead of costing a play). The active
// session belongs to one person and one overlay (`session:<key>`): the overlay shows it only to
// them, under that key (L6-06, N4).
import { useMemo } from 'react'
import { ulid } from 'ulid'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { ChildSettings, LearnTrack } from '../../shared/controls'
import type { Pack } from '../../shared/pack/schema'
import { usePlayerSettings } from '../data/controls'
import { record } from '../data/events'
import { useEvents, useProgress } from '../data/progress'
import { useSession } from '../data/session'
import { playDay } from '../domain/arcade/playDay'
import { playsToday, type DayPlays } from '../domain/arcade/plays'
import { travelDays, type Progress } from '../domain/arcade/progress'
import { stageZone, tripNow } from '../../shared/tripZone'
import { requeue } from '../domain/arcade/session'
import { isLearned, review, type Result } from '../domain/arcade/srs'
import type { Item, CardSetCtx, CardSetId } from '../domain/arcade/types'
import { learnSession, trackCardSet } from '../domain/learn'
import { warmupItems, type NewSound } from '../domain/newSounds'
import { rng } from '../domain/rng'
import { playsOf } from '../domain/players'
import { prizePool, prizesFor, rewardRules, type PoolPrize } from '../domain/rewards'
import { childWorld, drawPayout, ownedCollectibles, type Payout, type ReleaseTrip } from '../domain/release'
import { collectibleSet } from '../domain/collectibles'
import { ACT_CATALOG } from '../map/places/actCatalog'
import { placeId } from '../map/places/registry'
import { PRE_TRIP_SHARE, PRIZE_LEVEL_CHANCE, type RewardRules } from '../../shared/rewards'
import { useMinuteClock } from '../app/useTripNow'

export interface ActiveSession {
  /** Whose it is: the overlay never shows it to anyone else. */
  personId: string
  /** The overlay suffix it opens under (`session:<key>`): the machine, or `learn-<track>`. */
  key: string
  sessionId: string
  machine: CardSetId
  day: string
  items: Item[]
  index: number
  right: number
  combo: number
  xp: number
  /** Result per card so far (drives the progress segments). */
  results: ('right' | 'wrong')[]
  /** Card ids that reached "learned" during this capsule (for "メ learned"). */
  learned: string[]
  /** A Learn session (free, no prize) on this track, rather than an Arcade capsule. */
  track?: LearnTrack
}

interface ActiveStore {
  active: ActiveSession | null
  /** Paid capsules set aside when someone else started one, by person, so theirs still resumes. */
  parked: Record<string, ActiveSession>
  /** The last finished capsule, shown on the reward screen (with the prize it held, if any). */
  done: (ActiveSession & { sfx?: string; bonusXp: number; prize?: PoolPrize }) | null
  set(a: ActiveSession | null): void
  finish(d: ActiveStore['done']): void
  /** Back to this person's parked capsule under this key, if there is one. */
  resume(personId: string, key: string): boolean
  /**
   * The active session doesn't belong to this overlay (another person's, or another key): a Learn
   * session is free, so it goes; another person's paid capsule is parked for them (WI-12).
   */
  shelve(personId: string, key: string): void
}

export const useActive = create<ActiveStore>()(
  persist(
    (set, get) => ({
      active: null,
      parked: {},
      done: null,
      set: (active) =>
        set((s) => {
          // This person's session is the active one now, so it isn't parked as well.
          const parked = active ? Object.fromEntries(Object.entries(s.parked).filter(([id]) => id !== active.personId)) : { ...s.parked }
          // Someone else's paid capsule is kept for them (a Learn session is free: it just goes).
          const cur = s.active
          if (active && cur && cur.personId !== active.personId && !cur.track) parked[cur.personId] = cur
          return active?.index === 0 ? { active, parked, done: null } : { active, parked }
        }),
      finish: (done) => set({ done, active: null }),
      resume: (personId, key) => {
        const p = get().parked[personId]
        if (p?.key !== key) return false
        get().set(p)
        return true
      },
      shelve: (personId, key) =>
        set((s) => {
          const cur = s.active
          if (!cur || (cur.personId === personId && cur.key === key)) return {}
          if (cur.track) return { active: null }
          // This person's own capsule under another key stays put: the Arcade resumes it.
          if (cur.personId === personId) return {}
          return { active: null, parked: { ...s.parked, [cur.personId]: cur } }
        }),
    }),
    {
      name: 'tq-active-session',
      storage: createJSONStorage(() => sessionStorage),
      // v1: sessions carry their person and key. Older ones can't be matched to anyone, so they go.
      version: 1,
      migrate: () => ({ active: null, parked: {}, done: null }),
    },
  ),
)

/** The overlay suffix a session opens under: `session:<key>`. */
export const sessionKey = (machine: CardSetId, track?: LearnTrack): string => (track ? `learn-${track}` : machine)

/**
 * The Arcade for a person today: progress, the day's pool of plays (domain/arcade/plays), the
 * earning rules and, for a player, the prizes they could win. Someone who doesn't play (a
 * grown-up who hasn't joined in, WI-37) has no plays of their own: `player` is null.
 */
export function useArcade(pack: Pack, personId: string) {
  const now = useMinuteClock()
  const events = useEvents()
  const progress = useProgress(personId)
  const people = useSession((s) => s.people)
  const who = people?.find((p) => p.id === personId) ?? null
  const player = who && playsOf(who) ? who : null
  const settings = usePlayerSettings(player)
  const day = playDay(now)
  return useMemo(() => {
    const ctx: CardSetCtx = { pack, progress, day }
    const rules = rewardRules(events)
    const cap = settings?.arcade.maxPlays ?? 0
    const plays = playsToday(events, personId, day, cap, rules)
    const prizes = player ? prizesFor(prizePool(events), player) : []
    // Travel days don't break a streak (domain/arcade/progress `streak`).
    const restDays = travelDays(pack.itinerary.legs, (iso) => tripNow(pack, Date.parse(iso)).date)
    return { ctx, progress, day, plays, totalLeft: plays.left, rules, settings, prizes, events, restDays, player }
  }, [pack, progress, day, events, personId, settings, player])
}

// ---------------------------------------------------------------- WI-13: the twist is the reward

/** What the last twist paid, for the reveal (kept in sessionStorage so a reload still shows it). */
export interface TwistDone {
  personId: string
  twistId: string
  day: string
  payout: Payout<PoolPrize>
}

export const useTwist = create<{ last: TwistDone | null; set(last: TwistDone | null): void }>()(
  persist((set) => ({ last: null, set: (last) => set({ last }) }), { name: 'tq-twist', storage: createJSONStorage(() => sessionStorage), version: 1 }),
)

/** The trip as the release curve sees it: its stages on their islands, its dates and timezone. */
export function releaseTrip(pack: Pack): ReleaseTrip {
  return {
    start: pack.meta.startDate,
    end: pack.meta.endDate,
    tz: pack.meta.timezone,
    // B13: each stage keeps its own zone (shared/tripZone stageZone).
    stages: pack.itinerary.stages.map((s) => ({ id: s.id, place: placeId(s.spot), start: s.startDate, end: s.endDate, tz: stageZone(pack, s) })),
  }
}

/** A child's world today (WI-13: unlocks, keys, the curve), folded from their own events. */
export function useRewardWorld(pack: Pack, personId: string) {
  const now = useMinuteClock()
  const events = useEvents()
  const day = playDay(now)
  return useMemo(() => {
    const trip = releaseTrip(pack)
    const world = childWorld({ trip, catalog: ACT_CATALOG, events, childId: personId, day, now })
    return { trip, world, day, now }
  }, [pack, events, personId, day, now])
}

/**
 * e2e pins what a twist pays with sessionStorage `tq-prize-draw` (like `tq-force-full`): `always` a
 * real prize, `never` a collectible, `key` an act key (when one is left).
 */
function pinnedPayout(): 'always' | 'never' | 'key' | null {
  try {
    const pinned = sessionStorage.getItem('tq-prize-draw')
    return pinned === 'always' || pinned === 'never' || pinned === 'key' ? pinned : null
  } catch {
    return null
  }
}

/**
 * Spends a twist (WI-13a): a play from the day's pool, then straight to one payout (domain/release
 * `drawPayout`), recorded as `reward.paid` (a real prize also as `prize.won`, for Needs you). No card
 * round. Returns what it paid, for the reveal.
 */
export async function twist(pack: Pack, personId: string, view: { plays: DayPlays; events: readonly StoredLike[]; rules: RewardRules; prizes: readonly PoolPrize[] }): Promise<TwistDone | null> {
  if (view.plays.left <= 0) return null
  const now = Date.now()
  const day = playDay(now)
  const trip = releaseTrip(pack)
  const world = childWorld({ trip, catalog: ACT_CATALOG, events: view.events, childId: personId, day, now })
  const twistId = ulid().toLowerCase()
  await record('session.started', { machine: 'twist', sessionId: twistId, day, allowed: view.plays.left }, personId)
  const pin = pinnedPayout()
  const level = view.rules.prizes?.level ?? 'sometimes'
  const share = PRE_TRIP_SHARE[view.rules.release?.before ?? 'some']
  let payout: Payout<PoolPrize>
  const next = world.queue[0]
  if (pin === 'key' && next) {
    payout = { kind: 'key', ...next, label: ACT_CATALOG[next.place]?.find((a) => a.id === next.act)?.label ?? next.act }
  } else {
    const w = pin ? { ...world, queue: [] } : world
    payout = drawPayout({
      twistId,
      world: w,
      trip,
      day,
      catalog: ACT_CATALOG,
      share,
      prizes: view.prizes,
      prizeChance: pin === 'always' ? 1 : pin === 'never' ? 0 : PRIZE_LEVEL_CHANCE[level],
      sets: collectibleSet,
      owned: ownedCollectibles(view.events, personId),
    })
  }
  const base = { twistId, for: personId, day }
  if (payout.kind === 'key') await record('reward.paid', { ...base, kind: 'key', place: payout.place, act: payout.act }, personId)
  else if (payout.kind === 'prize') {
    const wonId = ulid().toLowerCase()
    await record('prize.won', { wonId, prizeId: payout.prize.id, for: personId, sessionId: twistId }, personId)
    await record('reward.paid', { ...base, kind: 'prize', prizeId: payout.prize.id, wonId }, personId)
  } else {
    await record('reward.paid', { ...base, kind: 'collectible', item: payout.item.id }, personId)
    if (payout.bonusKey) await record('reward.paid', { ...base, twistId: `${twistId}-bonus`, kind: 'key', place: payout.bonusKey.place, act: payout.bonusKey.act, bonus: true }, personId)
  }
  const done: TwistDone = { personId, twistId, day, payout }
  useTwist.getState().set(done)
  return done
}

interface StoredLike {
  id: string
  type: string
  at: number
  personId: string
  payload: unknown
}

/** Starts a Learn session (F1): free, so nothing is spent; its answers count to the daily goal. */
export function startLearn(ctx: CardSetCtx, track: LearnTrack, n: number, difficulty: ChildSettings['learn']['difficulty'], personId: string): ActiveSession | null {
  const items = learnSession(track, ctx, n, difficulty)
  if (!items.length) return null
  const machine = trackCardSet(track).id
  const active: ActiveSession = { personId, key: sessionKey(machine, track), sessionId: ulid(), machine, day: ctx.day, items, index: 0, right: 0, combo: 0, xp: 0, results: [], learned: [], track }
  useActive.getState().set(active)
  return active
}

/**
 * The new-sounds warm-up (WI-09): today's new characters, each asked as "which character makes
 * this sound?". It runs as a session of the first script's track, so its answers count and meet
 * the cards just as that track's would.
 */
export function startWarmup(ctx: CardSetCtx, track: LearnTrack, sounds: readonly NewSound[], difficulty: ChildSettings['learn']['difficulty'], personId: string): ActiveSession | null {
  const seed = [...`warmup|${ctx.day}|${sounds.map((s) => s.cardId).join(',')}`].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7)
  const items = warmupItems(ctx, sounds, difficulty === 'gentle', rng(seed))
  if (!items.length) return null
  const machine = trackCardSet(track).id
  const active: ActiveSession = { personId, key: sessionKey(machine, track), sessionId: ulid(), machine, day: ctx.day, items, index: 0, right: 0, combo: 0, xp: 0, results: [], learned: [], track }
  useActive.getState().set(active)
  return active
}

/** Ends a Learn session: recorded for the streak; no sticker, no prize. */
export async function finishLearn(a: ActiveSession, personId: string) {
  await record('learn.completed', { track: a.track, sessionId: a.sessionId, day: a.day, right: a.right, total: a.items.length }, personId)
  useActive.getState().finish({ ...a, bonusXp: 0 })
}

/**
 * Records an answer. A wrong answer, a skip or "Again" puts the card back later in this session
 * (domain/arcade/session `requeue`, WI-08), so it's asked again once its reveal has been seen.
 */
export async function answerCard(a: ActiveSession, progress: Progress, result: Result, xp: number, personId: string) {
  const item = a.items[a.index]!
  await record('card.answered', { machine: a.machine, sessionId: a.sessionId, cardId: item.cardId, result, xp, day: a.day, ...(a.track ? { track: a.track } : {}) }, personId)
  const before = progress.cards[item.cardId]
  const willLearn = !isLearned(before) && isLearned(review(before, result, a.day))
  const missed = result === 'wrong' || result === 'skip'
  const next: ActiveSession = {
    ...a,
    items: missed ? requeue(a.items, a.index) : a.items,
    right: a.right + (result === 'right' ? 1 : 0),
    combo: missed ? 0 : a.combo + (result === 'right' ? 1 : 0),
    xp: a.xp + xp,
    // A skip shows as a miss.
    results: Object.assign([...a.results], { [a.index]: result === 'skip' ? 'wrong' : result }),
    learned: willLearn ? [...a.learned, item.cardId] : a.learned,
  }
  useActive.getState().set(next)
  return next
}

export function advance(a: ActiveSession): ActiveSession {
  const next = { ...a, index: a.index + 1 }
  useActive.getState().set(next)
  return next
}

